import type { VehicleCfdSummary } from '@graphql/generated';
import {
  compareDivisionNamesForDisplay,
  compareRegionNamesForDisplay
} from '../../utils/region-sort.util';
import {
  activeDateOf,
  liveDateFromActive,
  resolveTranslationDate,
  sorpDateOf
} from './dashboard-dates.util';
import type {
  DashboardBrandGroup,
  DashboardRegionGroup,
  DashboardVehicle,
  DashboardVehicleTile,
  DashboardYearGroup,
  OrderGuideAttachment
} from './dashboard.types';
import type { OrderGuideNote } from './order-guide-note.util';

export function buildVehicleTile(
  vehicle: DashboardVehicle,
  cfdSummariesByVehicleId: ReadonlyMap<number, VehicleCfdSummary>,
  notesByVehicleId: ReadonlyMap<number, readonly OrderGuideNote[]> = new Map(),
  filesByVehicleId: ReadonlyMap<number, readonly OrderGuideAttachment[]> = new Map()
): DashboardVehicleTile {
  const activeDate = activeDateOf(vehicle.dates);

  return {
    vehicle,
    activeDate,
    liveDate: liveDateFromActive(activeDate),
    translationDate: resolveTranslationDate(vehicle.dates, vehicle.regionName),
    sorpDate: sorpDateOf(vehicle.dates),
    notes: [...(notesByVehicleId.get(vehicle.id) ?? [])],
    attachments: [...(filesByVehicleId.get(vehicle.id) ?? [])],
    cfd: cfdSummariesByVehicleId.get(vehicle.id) ?? null
  };
}

/**
 * Groups favorites into model year (newest first) → region (USA, Canada, then alphabetical) →
 * brand (alphabetical). Vehicles missing a model year are dropped: they cannot be placed under a
 * `{{year}}MY` heading, and the dashboard is organized entirely by year.
 */
export function groupFavoritesForDashboard(
  vehicles: readonly DashboardVehicle[],
  cfdSummaries: readonly VehicleCfdSummary[],
  notesByVehicleId: ReadonlyMap<number, readonly OrderGuideNote[]> = new Map(),
  filesByVehicleId: ReadonlyMap<number, readonly OrderGuideAttachment[]> = new Map()
): DashboardYearGroup[] {
  const summariesByVehicleId = new Map(cfdSummaries.map((s) => [s.vehicleId, s]));

  const byYear = new Map<number, DashboardVehicle[]>();
  for (const vehicle of vehicles) {
    if (vehicle.vehicleYear == null) {
      continue;
    }
    const bucket = byYear.get(vehicle.vehicleYear);
    if (bucket) {
      bucket.push(vehicle);
    } else {
      byYear.set(vehicle.vehicleYear, [vehicle]);
    }
  }

  return [...byYear.entries()]
    .sort(([a], [b]) => b - a)
    .map(([modelYear, yearVehicles]) => ({
      modelYear,
      regions: groupByRegion(yearVehicles, summariesByVehicleId, notesByVehicleId, filesByVehicleId)
    }));
}

function groupByRegion(
  vehicles: readonly DashboardVehicle[],
  summariesByVehicleId: ReadonlyMap<number, VehicleCfdSummary>,
  notesByVehicleId: ReadonlyMap<number, readonly OrderGuideNote[]>,
  filesByVehicleId: ReadonlyMap<number, readonly OrderGuideAttachment[]>
): DashboardRegionGroup[] {
  const byRegion = new Map<number, DashboardVehicle[]>();
  for (const vehicle of vehicles) {
    const bucket = byRegion.get(vehicle.regionId);
    if (bucket) {
      bucket.push(vehicle);
    } else {
      byRegion.set(vehicle.regionId, [vehicle]);
    }
  }

  return [...byRegion.entries()]
    .map(([regionId, regionVehicles]) => ({
      regionId,
      regionName: regionVehicles[0].regionName ?? `Region ${regionId}`,
      brands: groupByBrand(regionVehicles, summariesByVehicleId, notesByVehicleId, filesByVehicleId)
    }))
    .sort((a, b) => compareRegionNamesForDisplay(a.regionName, b.regionName));
}

function groupByBrand(
  vehicles: readonly DashboardVehicle[],
  summariesByVehicleId: ReadonlyMap<number, VehicleCfdSummary>,
  notesByVehicleId: ReadonlyMap<number, readonly OrderGuideNote[]>,
  filesByVehicleId: ReadonlyMap<number, readonly OrderGuideAttachment[]>
): DashboardBrandGroup[] {
  const byDivision = new Map<number, DashboardVehicle[]>();
  for (const vehicle of vehicles) {
    const bucket = byDivision.get(vehicle.divisionId);
    if (bucket) {
      bucket.push(vehicle);
    } else {
      byDivision.set(vehicle.divisionId, [vehicle]);
    }
  }

  return [...byDivision.entries()]
    .map(([divisionId, divisionVehicles]) => ({
      divisionId,
      divisionName: divisionVehicles[0].divisionName ?? `Division ${divisionId}`,
      tiles: divisionVehicles
        .slice()
        .sort(compareVehiclesForDisplay)
        .map((v) => buildVehicleTile(v, summariesByVehicleId, notesByVehicleId, filesByVehicleId))
    }))
    .sort((a, b) => compareDivisionNamesForDisplay(a.divisionName, b.divisionName));
}

function compareVehiclesForDisplay(a: DashboardVehicle, b: DashboardVehicle): number {
  if (a.vehicleSort !== b.vehicleSort) {
    return a.vehicleSort - b.vehicleSort;
  }
  return (a.vehicleName ?? '').localeCompare(b.vehicleName ?? '', undefined, {
    sensitivity: 'base'
  });
}
